/* eslint-disable react-doctor/no-giant-component */
import { useQuery } from "@tanstack/react-query";
import { cn } from "@wags/ui/ui-utils";
import { usePostHog } from "posthog-js/react";
import { lazy, Suspense } from "react";
import { buildFindSearchParams } from "../routes/browse-route-helpers";
import { FeedErrorState } from "@/components/shared/FeedErrorState";
import { FEED_LOADING_COPY, FeedSkeleton } from "./FeedSkeleton";
import { FeedRowList } from "./FeedRowList";
import { FindHeroBackground } from "../presentation/find-hero-background";
import { useOpenFeedSearch } from "../search-controls/feed-search-control";
import type { FindFeedData } from "./findFeedData";
import type { PendingFindFilters } from "../routes/find-route";
import type { FindSearch } from "../routes/find-route.contract.ts";
import { BrowseZone } from "./warmth/BrowseZone";
import { LiveTicker } from "./warmth/LiveTicker";
import { Footer } from "@/components/shared/Footer";
import {
    FOOTER_REVEAL_EDGE_PROPS,
    FOOTER_REVEAL_PROPS,
} from "@/components/shared/footerReveal";
import { Header } from "@/components/Header";
import { SECTION_CONTENT_CLASS } from "./listingRowLayout";
import {
    DesktopSearchBarV2,
    MobileResultsSearchV2,
} from "../search-controls/v2/lazySearchV2";
import {
    DesktopSearchBarV2Skeleton,
    MobileSearchBarV2Skeleton,
} from "../search-controls/v2/SearchBarSkeletons";
import { useFooterRevealed } from "@wags/tanstack-start/browser";
import { useScrollDirection } from "@wags/tanstack-start/browser";
import { afterFirstLcp } from "@wags/tanstack-start/after-first-lcp.ts";
import {
    getNetworkSources,
    NETWORK_SOURCES_TTL_MS,
} from "@/features/discovery";
import type { SearchParams } from "@/features/discovery";
import { type FindFeedRowKey } from "../routes/browse-route-helpers";
import { findFeedRowDefinitions } from "../feed/find-feed";
import { BOTTOM_NAV_RESERVE_CANCEL } from "@/lib/ui/bottomNav";
import { ReactTreeWhenNear } from "./react-tree-when-near";

const FindEmptyState = lazy(() =>
    import("./FindEmptyState").then((mod) => ({
        default: mod.FindEmptyState,
    })),
);
const BuildADogFinale = lazy(() =>
    import("./BuildADogFinale").then((mod) => ({
        default: mod.BuildADogFinale,
    })),
);

const heroBreedChips = [
    { id: "golden-retriever", name: "Golden Retriever" },
    { id: "shiba-inu", name: "Shiba Inu" },
    { id: "bulldog", name: "Bulldog" },
    { id: "poodle", name: "Poodle" },
    { id: "beagle", name: "Beagle" },
] as const;

const HERO_BREED_CHIP_CLASS =
    "relative h-9 shrink-0 cursor-pointer rounded-full border border-white/20 bg-white/10 px-3 md:px-3.5 before:absolute before:inset-x-0 before:-inset-y-[5px] md:before:hidden inline-flex items-center justify-center text-body-small text-white/90 transition-[background-color,border-color,transform] duration-100 [@media(hover:hover)]:hover:bg-white/20 [@media(hover:hover)]:hover:border-white/30 active:scale-97 active:duration-100 motion-reduce:active:scale-100";

export function FindFeedHeaders({
    searchParams,
    headerOnWhite,
    footerSentinelEl,
    isMobile,
    onSearchParamsChange,
}: {
    searchParams: FindSearch;

    headerOnWhite: boolean;
    footerSentinelEl: HTMLElement | null;
    isMobile: boolean;
    onSearchParamsChange: (next: FindSearch) => void;
}) {
    const footerRevealed = useFooterRevealed(footerSentinelEl, {
        enabled: isMobile,
    });
    const mobileSearchReady = useMobileSearchReady(isMobile);
    const hidden = useScrollDirection() === "down" || footerRevealed;
    const mobileSearchSkeleton = (
        <MobileSearchBarV2Skeleton
            variant="feed"
            wordmarkStateful={isMobile && !hidden}
        />
    );

    return (
        <>
            <header
                data-safari-chrome-surface
                className={cn(
                    "fixed top-0 left-0 right-0 z-50 flex px-6 pt-[calc(env(safe-area-inset-top)+20px)] pb-3 md:hidden",
                    "transition-[translate,background-color,border-color,color] duration-200 ease-out",
                    headerOnWhite ? "bg-true-white" : "bg-transparent",
                    hidden && "-translate-y-full",
                )}
            >
                <span
                    aria-hidden="true"
                    className={cn(
                        "pointer-events-none absolute inset-x-0 bottom-0 h-px bg-doberman-black/8 transition-opacity duration-200 ease-out",
                        headerOnWhite ? "opacity-100" : "opacity-0",
                    )}
                />
                <Suspense fallback={mobileSearchSkeleton}>
                    {isMobile && mobileSearchReady ? (
                        <MobileResultsSearchV2
                            searchParams={searchParams}
                            onSearchParamsChange={onSearchParamsChange}
                            onClearFeedContext={() => {}}
                            variant="feed"
                            showWordmark
                            wordmarkStateful={!hidden}
                        />
                    ) : (
                        mobileSearchSkeleton
                    )}
                </Suspense>
            </header>
            {!isMobile && (
                <Header
                    statefulLogo
                    className={cn(
                        "hidden md:block transition-colors duration-200 ease-out",
                        headerOnWhite ? "bg-true-white" : "bg-transparent",
                    )}
                    logoClassName={headerOnWhite ? undefined : "text-primary"}
                    accountLinkClassName={
                        headerOnWhite
                            ? undefined
                            : "text-true-white/90 [@media(hover:hover)]:hover:bg-true-white/10 [@media(hover:hover)]:hover:text-true-white focus-visible:ring-offset-0"
                    }
                    borderVisible={headerOnWhite}
                    signInPrimary
                    center={
                        <Suspense fallback={<DesktopSearchBarV2Skeleton />}>
                            <DesktopSearchBarV2
                                searchParams={searchParams}
                                onSearchParamsChange={onSearchParamsChange}
                                onClearFeedContext={() => {}}
                            />
                        </Suspense>
                    }
                />
            )}
        </>
    );
}

export function FindFeedContent({
    searchParams,
    resolvedLocation,
    feed,
    feedReadyPromise,
    heroCovered,
    contentRef,
    footerSentinelRef,
    onSearchParamsChange,
    onRequireLocation,
    locationChip,
}: {
    searchParams: FindSearch;
    resolvedLocation: { lat: number; lng: number } | null;
    locationChip?: React.ReactNode;
    feed: FindFeedData;
    feedReadyPromise: Promise<void> | undefined;

    heroCovered: boolean;
    contentRef: React.Ref<HTMLDivElement>;
    footerSentinelRef: React.Ref<HTMLDivElement>;
    onSearchParamsChange: (next: FindSearch) => void;
    onRequireLocation: (filters: PendingFindFilters | null) => void;
}) {
    const openFeedSearch = useOpenFeedSearch();
    const posthog = usePostHog();

    const {
        popularBreeds,
        findFeedQuery,
        feedLoadUi,
        retryFindFeed,
        nonEmptyRows,
        hasFeedDogs,
    } = feed;

    const isNoLocationEmpty = feedLoadUi === "empty" && !resolvedLocation;
    const feedAnnouncement = hasFeedDogs
        ? "Showing dogs near you"
        : feedLoadUi === "skeleton"
          ? FEED_LOADING_COPY
          : "";

    const buildSearchParams = (
        nextParams: Partial<
            Omit<SearchParams, "page" | "limit" | "offset">
        > = {},
    ): SearchParams | undefined =>
        buildFindSearchParams(searchParams, nextParams);

    const commitFilters = (
        nextParams: Partial<Omit<SearchParams, "page" | "limit">> = {},
    ) => {
        const next = buildSearchParams(nextParams);
        if (!next) return;
        onSearchParamsChange(next);
    };

    const handlePopularBreedClick = (breedId: string) => {
        posthog.capture("breed_chip_clicked", { breedId });
        if (!resolvedLocation) {
            onRequireLocation({ breedSlugs: [breedId] });
            return;
        }
        commitFilters({ breedSlugs: [breedId] });
    };

    const handleCollectionClick = (
        collectionId: string,
        filters: PendingFindFilters,
    ) => {
        posthog.capture("breed_collection_clicked", { collectionId });
        if (!resolvedLocation) {
            onRequireLocation(filters);
            return;
        }
        commitFilters(filters);
    };

    const handleListingRowClick = (rowKey: FindFeedRowKey) => {
        const rowDefinition = findFeedRowDefinitions.find(
            (definition) => definition.key === rowKey,
        );
        if (!rowDefinition) return;
        posthog.capture("listing_row_clicked", {
            rowKey,
            title: rowDefinition.title,
        });
        const filters = {
            ...rowDefinition.filters,
            feedContext: rowDefinition.title,
        };
        if (!resolvedLocation) {
            onRequireLocation(filters);
            return;
        }
        commitFilters(filters);
    };

    const { data: networkSources } = useQuery({
        queryKey: ["network-sources"],
        queryFn: () => getNetworkSources(),
        staleTime: NETWORK_SOURCES_TTL_MS,
    });

    const browseBand = (
        <div className={cn(SECTION_CONTENT_CLASS, "py-4 md:py-6")}>
            <BrowseZone
                onSelectCollection={handleCollectionClick}
                location={resolvedLocation}
                enableCounts={hasFeedDogs}
                breeds={popularBreeds}
                onSelectBreed={handlePopularBreedClick}

                onSearchAll={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    openFeedSearch();
                }}
            />
        </div>
    );

    return (
        <>
            <div className="relative">
                <div
                    className={cn(
                        // oxlint-disable-next-line shadcn/no-arbitrary-values -- hero height follows the viewport composition at each breakpoint
                        "sticky top-0 z-10 mb-16 flex min-h-[52svh] flex-col items-center justify-center overflow-hidden px-6 pt-36 pb-24 md:min-h-[60svh] md:pt-32",

                        heroCovered && "invisible",
                    )}
                >
                    <FindHeroBackground paused={heroCovered} />
                    <div className="relative z-10 w-full flex max-w-7xl flex-col items-center">
                        <h1 className="text-center">
                            <span className="text-display-title text-white">
                                Find a dog that <br className="md:hidden" />
                                fits your life.
                            </span>
                        </h1>

                        {/* oxlint-disable-next-line shadcn/no-arbitrary-values -- xl clamp slope has no named type role */}
                        <p className="mt-3 md:mt-5 text-center text-body-small md:text-base xl:text-[clamp(1rem,0.5rem+0.625vw,1.25rem)] text-white/75">
                            2M+ dogs from shelters, rescues, and breeders
                        </p>
                        <div className="mt-8 flex w-screen items-center gap-2 overflow-x-auto scrollbar-none px-6 py-1 md:w-full md:max-w-200 md:justify-center md:px-0">
                            {heroBreedChips.map((breed) => (
                                <button
                                    key={breed.id}
                                    type="button"
                                    onClick={() =>
                                        handlePopularBreedClick(breed.id)
                                    }
                                    className={HERO_BREED_CHIP_CLASS}
                                >
                                    <span className="relative top-[0.5px]">
                                        {breed.name}
                                    </span>
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={openFeedSearch}
                                className={HERO_BREED_CHIP_CLASS}
                            >
                                <span className="relative top-[0.5px]">
                                    All breeds
                                </span>
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    ref={contentRef}
                    // oxlint-disable-next-line shadcn/no-arbitrary-values -- large-viewport height plus the 6rem overlap needs calc; upward-cast shadow, and elevation tokens only cast downward
                    className="relative z-20 -mt-24 min-h-[calc(100lvh+6rem)] rounded-surface bg-true-white w-full shadow-[0_-4px_16px_rgba(0,0,0,0.04)]"
                >
                    <div
                        ref={footerSentinelRef}
                        aria-hidden
                        className="absolute bottom-0 h-0"
                    />
                    <div
                        className={cn(
                            "flex flex-col gap-y-8 pb-12 md:pt-16",
                            isNoLocationEmpty ? "pt-6" : "pt-12",
                        )}
                    >
                        <div
                            className={cn(
                                SECTION_CONTENT_CLASS,
                                isNoLocationEmpty && "hidden md:block",
                            )}
                        >
                            <LiveTicker sources={networkSources} />
                        </div>
                        {feedLoadUi === "failure" ? (
                            <div className={SECTION_CONTENT_CLASS}>
                                <FeedErrorState onRetry={retryFindFeed} />
                            </div>
                        ) : feedLoadUi === "empty" ? (
                            <div
                                className={cn(
                                    SECTION_CONTENT_CLASS,
                                    "relative",
                                    isNoLocationEmpty && "py-4 md:py-20",
                                )}
                            >
                                <Suspense fallback={null}>
                                    <FindEmptyState
                                        searchParams={searchParams}
                                        currentMaxDistance={
                                            searchParams.maxDistanceMiles
                                        }
                                        resolvedLocation={resolvedLocation}
                                        onRequireLocation={() =>
                                            onRequireLocation(null)
                                        }
                                    />
                                </Suspense>
                            </div>
                        ) : null}
                        <output className="sr-only" aria-live="polite">
                            {feedAnnouncement}
                        </output>
                        {(feedLoadUi === "skeleton" || hasFeedDogs) && (
                            <Suspense fallback={<FeedSkeleton />}>
                                <FeedRowList
                                    rows={nonEmptyRows}
                                    feedQuery={findFeedQuery}
                                    feedReadyPromise={feedReadyPromise}
                                    browseBand={browseBand}
                                    firstRowAccessory={locationChip}
                                    onRowClick={handleListingRowClick}
                                />
                            </Suspense>
                        )}
                    </div>
                    {hasFeedDogs && (
                        <ReactTreeWhenNear>
                            <Suspense fallback={null}>
                                <BuildADogFinale searchParams={searchParams} />
                            </Suspense>
                        </ReactTreeWhenNear>
                    )}
                </div>
            </div>
            <div {...FOOTER_REVEAL_EDGE_PROPS} />
            <div
                {...FOOTER_REVEAL_PROPS}
                className={cn(
                    "sticky bottom-0 z-0 -mt-12",
                    BOTTOM_NAV_RESERVE_CANCEL,
                )}
            >
                <ReactTreeWhenNear keepServerMarkup>
                    <Footer surfaceClassName="bg-transparent" />
                </ReactTreeWhenNear>
            </div>
        </>
    );
}

function useMobileSearchReady(enabled: boolean) {
    const { data: ready = false } = useQuery({
        queryKey: ["find-mobile-search-ready"],
        queryFn: ({ signal }) =>
            new Promise<boolean>((resolve) => {
                let settled = false;
                const finish = () => {
                    if (settled || signal.aborted) return;
                    settled = true;
                    document.removeEventListener("pointerdown", finish);
                    resolve(true);
                };
                signal.addEventListener(
                    "abort",
                    () => {
                        settled = true;
                        document.removeEventListener("pointerdown", finish);
                    },
                    { once: true },
                );
                document.addEventListener("pointerdown", finish, {
                    once: true,
                });
                void afterFirstLcp(signal).then(finish);
            }),
        enabled: enabled && typeof window !== "undefined",
        staleTime: Number.POSITIVE_INFINITY,
    });
    return ready;
}
